/**
 * Feature Flags Slice
 *
 * GW-919: Zustand slice for feature flags state management.
 * GW-8339: Flags are read from the static, deploy-time-injected per-environment
 * feature-flags.js (window.__FEATURE_FLAGS__) instead of gw-admin-api. The read
 * is synchronous, but is still wrapped in the same loading/error/TTL/batch
 * machinery as before so existing consumers (useFeatureFlag, FeatureFlagGuard)
 * are unaffected.
 * Stores feature flag values by name with per-flag loading and error states.
 */

import { clearError as clearOpError, isOperationLoading } from '@gw/ui';
import { KNOWN_FEATURE_FLAGS } from '@gw/ui/types';

import { getStaticFeatureFlag } from '@/config/staticFeatureFlags';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type {
  FeatureFlagResponse,
  KnownFeatureFlags,
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

/**
 * GW-8339: Reads a flag's static value synchronously, wrapped in a resolved
 * Promise to fit the existing async fetch call sites unchanged.
 */
function getFeatureFlag(flagName: string): Promise<FeatureFlagResponse> {
  return Promise.resolve({ flagName, enabled: getStaticFeatureFlag(flagName) });
}

export interface FeatureFlagsSlice {
  // State - store feature flag values by name
  featureFlags: Record<string, FeatureFlagResponse>;
  featureFlagsIsLoading: LoadingStates;
  featureFlagsHasLoaded: LoadingStates;
  featureFlagsLastLoaded: LoadedTimestamps;
  featureFlagsErrors: ErrorStates;
  featureFlagsErrorStatuses: ErrorStatuses;

  // Batch loading state
  featureFlagsBatchLoading: boolean;
  featureFlagsBatchGeneration: number;

  // Actions
  fetchFeatureFlag: (flagName: KnownFeatureFlags) => Promise<FeatureFlagResponse | null>;
  fetchAllKnownFlags: () => Promise<void>;
  clearFeatureFlags: () => void;
  clearFeatureFlagError: (flagName: string) => void;
}

const initialFeatureFlagsState = {
  featureFlags: {},
  featureFlagsIsLoading: {},
  featureFlagsHasLoaded: {},
  featureFlagsLastLoaded: {},
  featureFlagsErrors: {},
  featureFlagsErrorStatuses: {},
  featureFlagsBatchLoading: false,
  featureFlagsBatchGeneration: 0,
};

export const createFeatureFlagsSlice: StateCreator<FeatureFlagsSlice, [], [], FeatureFlagsSlice> = (
  set,
  get
) => ({
  ...initialFeatureFlagsState,

  fetchFeatureFlag: async (flagName: KnownFeatureFlags) => {
    const currentState = get();
    const operationKey = `fetchFeatureFlag:${flagName}`;

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.featureFlagsIsLoading, operationKey)) {
      return currentState.featureFlags[flagName] ?? null;
    }

    // Guard: Return cached data if already loaded and not stale (GW-4687: 5 min TTL)
    if (currentState.featureFlagsHasLoaded[operationKey] && currentState.featureFlags[flagName]) {
      const loadedAt = currentState.featureFlagsLastLoaded?.[operationKey] ?? 0;
      // Treat missing timestamp (0) as fresh — batch-loaded flags may not have timestamps
      if (loadedAt === 0 || Date.now() - loadedAt < 5 * 60 * 1000) {
        return currentState.featureFlags[flagName];
      }
    }

    await createAsyncSliceAction<FeatureFlagsSlice, FeatureFlagResponse>(
      operationKey,
      () => getFeatureFlag(flagName),
      (flag) => ({
        featureFlags: { ...get().featureFlags, [flagName]: flag },
      }),
      set,
      get,
      { prefix: 'featureFlags' }
    )();

    return get().featureFlags[flagName] ?? null;
  },

  clearFeatureFlags: () => {
    set({
      ...initialFeatureFlagsState,
      featureFlagsBatchGeneration: get().featureFlagsBatchGeneration + 1,
    });
  },

  clearFeatureFlagError: (flagName: string) => {
    const state = get();
    const operationKey = `fetchFeatureFlag:${flagName}`;
    set({
      featureFlagsErrors: clearOpError(state.featureFlagsErrors, operationKey),
    });
  },

  fetchAllKnownFlags: async () => {
    const generationAtStart = get().featureFlagsBatchGeneration;
    set({ featureFlagsBatchLoading: true });

    const results = await Promise.allSettled(
      KNOWN_FEATURE_FLAGS.map((flagName) =>
        getFeatureFlag(flagName).then((flag) => ({ flagName, flag }))
      )
    );

    // If generation changed during fetch (e.g., org change cleared flags), discard results
    if (get().featureFlagsBatchGeneration !== generationAtStart) {
      set({ featureFlagsBatchLoading: false });
      return;
    }

    const flags = { ...get().featureFlags };
    const hasLoaded = { ...get().featureFlagsHasLoaded };
    const lastLoaded = { ...get().featureFlagsLastLoaded };
    const now = Date.now();

    for (const result of results) {
      if (result.status === 'fulfilled') {
        const { flagName, flag } = result.value;
        const operationKey = `fetchFeatureFlag:${flagName}`;
        flags[flagName] = flag;
        hasLoaded[operationKey] = true;
        lastLoaded[operationKey] = now;
      }
    }

    set({
      featureFlags: flags,
      featureFlagsHasLoaded: hasLoaded,
      featureFlagsLastLoaded: lastLoaded,
      featureFlagsBatchLoading: false,
    });
  },
});
